const _excluded = ["items", "groupedItems", "itemToString", "defaultHighlightedIndex", "target", "onInputValueChange", "layoutDependency", "getFilteredItems", "getMenuProps", "initialInputValue", "stateReducer"],
  _excluded2 = ["isOpen", "getInputProps", "getItemProps", "getToggleButtonProps", "selectItem", "getMenuProps", "openMenu", "highlightedIndex", "selectedItem"],
  _excluded3 = ["onChange", "ref"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], t.indexOf(o) >= 0 || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.indexOf(n) >= 0) continue; t[n] = r[n]; } return t; }
import { useCallback, useLayoutEffect, useRef, useMemo } from 'react';
import { useCombobox } from 'downshift';
import { mergeRefs, useControlled, useForwardedRef } from '@adsk/alloy-react-helpers';
import { useOverlay } from '@adsk/alloy-react-overlay';
import useFilteredItems from './useFilteredItems';
import useGroupedItems from './useGroupedItems';
import useVirtualProps from './useVirtualProps';
import { checkReferenceEquality, defaultLabelGetter, isGroupHeader, wrapGetItemProps } from '../helpers';
/**
 * A hook used to manage all logic needed for an Autocomplete (searchable) dropdown.
 */
const useAutocomplete = _ref => {
  var _targetRef$current;
  let {
      items,
      groupedItems,
      itemToString = defaultLabelGetter,
      defaultHighlightedIndex = 0,
      target,
      onInputValueChange: onInputValueChangeProp,
      layoutDependency,
      getFilteredItems,
      getMenuProps: getMenuPropsProp,
      initialInputValue,
      stateReducer: stateReducerProp
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  const maybeGroupedItems = items || groupedItems;
  checkReferenceEquality(_objectSpread(_objectSpread({}, props), {}, {
    items: maybeGroupedItems
  }));
  const targetRef = useForwardedRef(target || null);
  const [inputValue, setInputValue] = useControlled(initialInputValue || '', props.inputValue);
  const {
    flatItems,
    indexGetter: groupedIndexGetter,
    getMenuItems
  } = useGroupedItems({
    maybeGroupedItems
  });
  const {
    filteredItems
  } = useFilteredItems({
    items: flatItems,
    getFilteredItems,
    itemToString,
    inputValue
  });
  const autocompleteStateReducer = useCallback((state, _ref2) => {
    let {
      changes,
      type
    } = _ref2;
    switch (type) {
      case useCombobox.stateChangeTypes.InputClick:
        if (state.isOpen) {
          // if the user clicks on the input while the menu is open, then we should keep the menu open with its highlighted index
          return _objectSpread(_objectSpread({}, changes), {}, {
            highlightedIndex: state.highlightedIndex,
            isOpen: true
          });
        }
        return changes;
      case useCombobox.stateChangeTypes.InputChange:
        if (changes.inputValue === undefined) {
          return changes;
        }
        if (changes.inputValue === '') {
          // if the input value is empty, then we should clear the selected item
          return _objectSpread(_objectSpread({}, changes), {}, {
            selectedItem: null
          });
        }
        if (changes.inputValue.length === 1 && state.inputValue.length > 2) {
          // This happens when users select-all and type a new character
          // This causes the minorest of bugs where if you select all but one character and delete it, it will clear the selected value
          return _objectSpread(_objectSpread({}, changes), {}, {
            selectedItem: null
          });
        }
        return changes;
      case useCombobox.stateChangeTypes.FunctionOpenMenu:
        if (changes.isOpen && state.selectedItem) {
          // if the user is opening the menu while an item it selected, then let's make sure
          // that the highlighted index is set to the selected index
          return _objectSpread(_objectSpread({}, changes), {}, {
            highlightedIndex: filteredItems.indexOf(state.selectedItem)
          });
        }
        return changes;
      case useCombobox.stateChangeTypes.FunctionCloseMenu:
      case useCombobox.stateChangeTypes.InputBlur:
      case useCombobox.stateChangeTypes.ToggleButtonClick:
        if (!changes.isOpen && state.selectedItem) {
          // if the user is closing the menu while an item it selected, then let's make sure
          // that input value is reset to the selected item, discarding their incomplete search
          return _objectSpread(_objectSpread({}, changes), {}, {
            inputValue: itemToString(state.selectedItem)
          });
        }
        return changes;
      default:
        return changes;
    }
  }, [filteredItems, itemToString]);
  const onInputValueChange = useCallback(args => {
    if (
    // This type gets triggered on render in multi autocomplete scenarios which provide selectedItem as null
    // This sets the input value to '' and breaks `initialInputValue`
    args.type !== useCombobox.stateChangeTypes.ControlledPropUpdatedSelectedItem) {
      if (props.inputValue === undefined) {
        // controlled inputValue should take priority
        setInputValue(args.inputValue || '');
      }
    }
    onInputValueChangeProp === null || onInputValueChangeProp === void 0 ? void 0 : onInputValueChangeProp(args);
  }, [onInputValueChangeProp, props.inputValue, setInputValue]);
  const stateReducer = useCallback((state, actionAndChanges) => {
    const updatedState = autocompleteStateReducer(state, actionAndChanges);
    const customUpdatedState = stateReducerProp === null || stateReducerProp === void 0 ? void 0 : stateReducerProp(state, actionAndChanges);
    return _objectSpread(_objectSpread(_objectSpread({}, actionAndChanges.changes), updatedState), customUpdatedState);
  }, [autocompleteStateReducer, stateReducerProp]);
  const _useCombobox = useCombobox(_objectSpread({
      items: filteredItems,
      itemToString,
      initialInputValue,
      inputValue,
      onInputValueChange,
      defaultHighlightedIndex,
      stateReducer
    }, props)),
    {
      isOpen: downshiftIsMenuOpen,
      getInputProps: rawGetInputProps,
      getItemProps: rawGetItemProps,
      getToggleButtonProps,
      selectItem,
      getMenuProps: rawGetMenuProps,
      openMenu,
      highlightedIndex,
      selectedItem
    } = _useCombobox,
    useComboboxReturnValue = _objectWithoutProperties(_useCombobox, _excluded2);
  const {
    show: useOverlayIsOpen,
    targetProps,
    overlayProps
  } = useOverlay(_objectSpread({
    placement: 'bottom-start',
    show: downshiftIsMenuOpen,
    target: targetRef,
    layoutDependency,
    isOverlayConditionallyRendered: false
  }, props));
  const onComposition = useRef(false);

  // https://github.com/facebook/react/issues/3926#issuecomment-929799564
  const getOnChangeWithCompositionSupport = useCallback(_ref3 => {
    let {
      onChangeProp
    } = _ref3;
    return event => {
      var _event$target;
      setInputValue(event === null || event === void 0 ? void 0 : (_event$target = event.target) === null || _event$target === void 0 ? void 0 : _event$target.value);

      // handle parent onChange
      if (!onComposition.current) {
        onChangeProp === null || onChangeProp === void 0 ? void 0 : onChangeProp(event);
      }
    };
  }, [setInputValue]);
  const onCompositionStart = useCallback(() => onComposition.current = true, []);
  const onCompositionEnd = useCallback(() => onComposition.current = false, []);
  const getMenuProps = useCallback((options, otherOptions) => {
    const temp = getMenuPropsProp ? rawGetMenuProps(getMenuPropsProp(_objectSpread(_objectSpread({}, overlayProps), options), otherOptions), otherOptions) : rawGetMenuProps(_objectSpread(_objectSpread({}, overlayProps), options), otherOptions);
    return temp;
  }, [getMenuPropsProp, overlayProps, rawGetMenuProps]);
  const getInputProps = useCallback(options => {
    const _rawGetInputProps = rawGetInputProps(options),
      {
        onChange: rawOnChange,
        ref
      } = _rawGetInputProps,
      inputProps = _objectWithoutProperties(_rawGetInputProps, _excluded3);
    const onChange = getOnChangeWithCompositionSupport({
      onChangeProp: rawOnChange
    });
    return _objectSpread({
      onChange,
      onCompositionStart,
      onCompositionEnd,
      ref: ref
    }, inputProps);
  }, [getOnChangeWithCompositionSupport, onCompositionEnd, onCompositionStart, rawGetInputProps]);
  const menuItems = useMemo(() => getMenuItems(filteredItems), [filteredItems, getMenuItems]);
  const defaultIndexGetter = useCallback(item => menuItems.indexOf(item), [menuItems]);
  const getItemProps = useCallback(args => wrapGetItemProps({
    getItemProps: rawGetItemProps,
    highlightedIndex,
    selectedItem,
    indexGetter: groupedIndexGetter || defaultIndexGetter
  })(args), [defaultIndexGetter, groupedIndexGetter, highlightedIndex, rawGetItemProps, selectedItem]);
  useLayoutEffect(() => {
    mergeRefs(targetProps.ref, targetRef);
  }, [targetProps, targetRef]);
  const virtualProps = useVirtualProps({
    menuItems,
    isGroupHeader
  });
  return _objectSpread({
    controlProps: {
      ref: targetRef,
      isAutocomplete: true,
      isOpen: useOverlayIsOpen,
      getInputProps,
      getToggleButtonProps: getToggleButtonProps,
      openMenu,
      selectedItem,
      itemToString,
      selectItem: selectItem
    },
    menuProps: {
      isOpen: useOverlayIsOpen,
      isAutocomplete: true,
      menuItems,
      getItemProps,
      getMenuProps,
      highlightedIndex,
      selectedItem,
      width: (_targetRef$current = targetRef.current) === null || _targetRef$current === void 0 ? void 0 : _targetRef$current.offsetWidth,
      selectItem,
      isGroupHeader
    },
    /**
     * Props to be provided to `react-virtual`'s `useVirtual` hook when using virtualization.
     */
    virtualProps,
    selectItem
  }, useComboboxReturnValue);
};
export default Object.assign(useAutocomplete, {
  stateChangeTypes: useCombobox.stateChangeTypes
});
//# sourceMappingURL=useAutocomplete.js.map